import dayjs from 'dayjs'
import 'dayjs/locale/es'
import 'dayjs/locale/it'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import 'dayjs/locale/de'
import 'dayjs/locale/nl'

import { Themes } from 'styles/constants'
import { ExtraLabel, MainLabel } from 'styles/sharedStyles'

import {
  ReturnReason,
  CountryCode,
  PRIVALIA_SITE_ID,
  SiteId,
  DeliveryPeriod,
  RETURN_NEGATIVE_REFUND_SUBJECT_ID,
  RETURN_SPECIAL_CARRIER_SUBJECT_ID,
  RETURN_SUBJECT_ID,
  RETURN_NOT_MODIFIABLE_SUBJECT_ID,
  RETURN_EXPIRED_SUBJECT_ID,
  RETURN_DROP_POINT_SUBJECT_ID,
  CONTACT_FORM_FLOW_STEPS,
  RETURN,
  ReturnFlowSteps,
  VEEPEE_PURPLE_SITE_ID,
  VEX_SITE_IDS,
  Device,
  ContactMethods,
  RECYCLE_CONTACT_REASONS,
  NOTIFICATION_CONTAINER,
  DeliveryType,
  WalletScreen,
  BrandAlertsSubs,
  SPECIAL_DELIVERY_TYPES,
  FormType,
  CultureCode,
  CarrierIdValue,
} from '../constants'

import {
  AddressType,
  ConfigApiType,
  ContactMethodType,
  ContactReasonsType,
  FieldErrorType,
  HelpOrderType,
  HomePickupDateType,
  ItemType,
  OrderListType,
  ParsedOptionsType,
  ParsedOrderType,
  PastOrder,
  PastOrderItems,
  PeriodMapping,
  ReturnDetailType,
  ReturnType,
  SelectedItemType,
  StatusTimelineEventType,
} from './types'

export const getNextStep = (step?: number, currentStep?: number) => (typeof step === 'number' ? step : currentStep + 1)

export const cultureToCountryCode = (culture: CultureCode | string) => {
  if (!culture) return ''
  if (culture === CultureCode.ES_MX) return CountryCode.SPAIN
  if (culture === CultureCode.IT_CH) return CountryCode.ITALY

  return culture.substr(-2)
}

export const cultureToLanguageCode = (culture: string) => (culture ? culture.substr(0, 2) : '')
export const capitalize = (value: string) => value.substr(0, 1).toUpperCase() + value.substr(1)

const isArbitrage = (items: ItemType[]) => !!items.find((item) => item.checked && item.reason && item.reason.isArbitrage)

const isInvoluntary = (items: ItemType[]) => !!items.find((item) => item.checked && item.reason && item.reason.isVpError)

const isVoluntary = (items: ItemType[]) =>
  !!items.find((item) => item.checked && item.reason && !item.reason.isVpError && !item.reason.isArbitrage)

export const getShipmentType = (items: ItemType[]) => {
  if (isArbitrage(items)) return DeliveryType.ARBITRATION
  if (isInvoluntary(items)) return ReturnReason.INVOLUNTARY
  if (isVoluntary(items)) return ReturnReason.VOLUNTARY

  return null
}

export const getShouldDisplayReturnFee = (items: ItemType[]) => getShipmentType(items) === ReturnReason.VOLUNTARY

/* istanbul ignore next */
export const ROUTES = {
  HOME: '/',
  MY_ACCOUNT: '/gr/myaccount',
  AUTHENTICATION: '/authentication',
  ORDERS: '/gr/my-orders',
  ORDER_CANCEL: (orderId: string) => `/cancel/${orderId}`,
  ORDER_RETURN: (orderId: string) => `/return/${orderId}`,
  FOOTER: (siteId: number) => `/ns-head/Structure/Footer?SiteId=${siteId}`,
  CART: () => '/cart',
  PAST_ORDER_DETAILS: (orderId: string) => `/privaliapo/order/${orderId}`,
  PAST_ORDER_CONTACT: (orderId: string) => `/privaliapo/contact/${orderId}`,
  PAST_ORDER_RETURN: (orderId: string) => `/privaliapo/return/${orderId}`,
  VEX_PAST_ORDER_DETAILS: (orderId: string) => `/vexpo/order/${orderId}`,
  HELP_FORM_URL: '/helpform',
  GR_HELP_FORM_URL: '/gr/helpform',
  REASON_SELECTOR: '/reasonselector',
  CONTACT_SELECTOR: '/contactselector',
  ADDRESSES: '/gr/myaccount/addresses',
  WALLET: '/wallet',
  VOUCHERS: '/gr/myaccount/vouchers-promotions',
  CHAT: (reasonId: number, order: HelpOrderType) =>
    `/postsales/chat?reason=${reasonId}${order?.orderId ? `&order=${order.orderId}&isPastOrder=${order.isPastOrder}` : ''}`,
  NOT_FOUND: '/404',
  SPONSORSHIP: '/gr/sponsorship',
  RECYCLE: (env) => (env === 'production' ? 'https://recycle.veepee.fr/' : 'https://recycle-preprod.veepee.fr/'),
  PERSONAL_DATA: '/gr/myaccount/memberdata',
  PREMIUM: '/checkout/loyalty',
  COMMUNICATIONS: '/gr/communications',
  FAVOURITES: '/memberaccount/favourites',
  MKP_HELP_ORDER: (orderId: string) => `/memberaccount/HelpMobile/HelpMobileOrderPage/${orderId}`,
  VP4: '/VP4/Help/Default.aspx',
  ROSEDEALS: '/gr/rosedeals',
}

export const goOrders = () => {
  window.location.assign(ROUTES.ORDERS)
}

export const goCart = () => {
  window.location.href = ROUTES.CART()
}

export const goHome = () => {
  window.location.href = ROUTES.HOME
}

export const goMyAccount = () => {
  window.location.assign(ROUTES.MY_ACCOUNT)
}

export const goOrderMaintenance = (url: string) => {
  window.location.href = url
}

export const goToGrHelpForm = () => {
  window.location.href = ROUTES.GR_HELP_FORM_URL
}

export const goSponsorship = () => {
  window.location.href = ROUTES.SPONSORSHIP
}
export const goPersonalData = () => {
  window.location.assign(ROUTES.PERSONAL_DATA)
}
export const goPremium = () => {
  window.location.assign(ROUTES.PREMIUM)
}
export const goLegacyFavourites = () => {
  window.location.assign(ROUTES.FAVOURITES)
}
export const openChatTab = (reasonId: number, order: HelpOrderType) => {
  window.open(ROUTES.CHAT(reasonId, order), '_blank')
}
export const redirectToLogoutPage = () => {
  window.location.assign('/authentication/logout/')
}

export const openNewTab = (url: string) => {
  window.open(url, '_blank')
}

export const goRecycle = () => {
  const env = process.env.CUSTOM_ENV
  window.location.href = ROUTES.RECYCLE(env)
}

export const goMkpHelpOrder = (orderId: string) => {
  window.location.assign(ROUTES.MKP_HELP_ORDER(orderId))
}
export const goVP4 = () => {
  window.location.assign(ROUTES.VP4)
}
export const goCancelOrder = (router, orderId: string) => {
  router.push(ROUTES.ORDER_CANCEL(orderId))
}
export const goReturnOrder = (router, orderId: string) => {
  router.push(ROUTES.ORDER_RETURN(orderId))
}

export const openInvoice = (orderId: string) => {
  window.location.assign(WEBSERVICES.POSTSALES.ORDERS.GET_INVOICE(orderId))
}
export const openVoucher = (orderId: string) => {
  window.location.assign(WEBSERVICES.POSTSALES.ORDERS.GET_VOUCHER(orderId))
}
export const openReturnDocument = (orderId: string) => {
  window.location.assign(WEBSERVICES.POSTSALES.ORDERS.GET_RETURN_DOCUMENT(orderId))
}

/* istanbul ignore next */
export const WEBSERVICES = {
  AUTHENTICATION: {
    WEBVALIDATE: '/ns-sd/frontservices/2.0/authentication/webvalidate',
  },
  ABTESTING: {
    BASE_URL: 'https://data.services.preprod.vente-privee.com/frontservices/api-distribution/v1/',
    GET_VARIATIONS: (userId, siteId) => `${WEBSERVICES.ABTESTING.BASE_URL}variations/${userId}?siteId=${siteId}`,
  },
  POSTSALES: {
    SEND_CONTACTFORM: '/memberaccount/api/privalia/ticket/',
    RETURNS: {
      BASE_URL: '/api/returns/',
      GET_INVOICE: (orderId: string) => `${WEBSERVICES.POSTSALES.RETURNS.BASE_URL}${orderId}/getReturnDocument`,
      HAS_INVOICE: (orderId: string) => `${WEBSERVICES.POSTSALES.RETURNS.BASE_URL}${orderId}/hasReturnDocument`,
      GET_ORDER: (orderId: string) => `${WEBSERVICES.POSTSALES.RETURNS.BASE_URL}${orderId}/order`,
      SEND_DECLARATION: (orderId: string) => `${WEBSERVICES.POSTSALES.RETURNS.BASE_URL}${orderId}/declarationv2`,
      GET_REASONS: (orderId: string) => `${WEBSERVICES.POSTSALES.RETURNS.BASE_URL}${orderId}/productReturnReasons`,
      RETURN_POSSIBILITIES: (orderId: string) => `/api/ares/v3/returnPossibilities/${orderId}`,
      RETURN_FLOW: (orderId: string) => `/api/ares/v3/returnFlowAvailable/${orderId}`,
      RETURN_FEE: (orderId: string) => `/api/ares/v1/return-fee/${orderId}`,
    },
    PRIVALIA_PASTORDERS: {
      GET_ORDERS: (memberId: string) => `/memberaccount/api/privalia/orders/${memberId}`,
      GET_ORDER: (orderId: string) => `/memberaccount/api/privalia/orderdetails/${orderId}`,
      RETURN: '/api/ares/ppo-returns/homepickup-data',
      RETURN_REASONS: '/api/ares/ppo-returns/return-reasons',
    },
    VEX_PASTORDERS: {
      GET_ORDER: (orderId: string) => `/memberaccount/api/vex/orderdetails/${orderId}`,
    },
    MKP_ORDERS: {
      GET_ORDER: (orderId: string) => `/api/postsale/v1/order/MarketplaceMobile/${orderId}`,
    },
    ORDERS: {
      GET_INVOICE: (orderId: string) => `/api/postsale/v1/order/${orderId}/invoice?embedded=false`,
      GET_ORDER_DETAILS: (orderType: string, orderId: string) => `/api/postsale/v1/order/${orderId}/${orderType}`,
      GET_RETURN_DOCUMENT: (orderId: string) => `/api/postsale/v1/order/${orderId}/returnDocument`,
      GET_VOUCHER: (orderId: string) => `/api/postsale/v1/order/${orderId}/voucher`,
    },
    HELP: {
      GET_ORDERS: '/api/postsales/v1/Orders/ContactForm',
      GET_HELP_CONTACT_REASONS: '/api/postsales/v1/Contact/contact-reasons',
      GET_SALES: '/api/postsales/v1/Sales/ContactForm',
    },
    CONFIG: {
      LIST: '/api/postsales/config/v1/features/list/postsales/web/',
      CHAT: '/api/postsales/config/v1/features/target/postsales/web/live-chat?featureVersion=1',
      WALLET: '/api/postsales/config/v1/features/target/postsales/web/wallet?featureVersion=1',
      VOUCHERS: '/api/postsales/config/v1/features/target/postsales/web/vouchers?featureVersion=1',
      CANCEL: '/api/postsales/config/v1/features/target/postsales/web/cancel-order?featureVersion=1',
      CONTACT_METHODS: '/api/postsales/config/v1/contact-methods',
      FAQS: '/api/postsales/config/v1/faqs?platform=web',
      LIFE2_MENU: '/api/postsale/v1/life2menu',
    },
    WALLET: {
      DETAILS: '/api/postsales/v1/Wallet/details',
      IBAN_INFO: '/api/postsales/v1/Wallet/iban-info',
      PAYMENT_METHOD: {
        DELETE: (paymentId: string) => `/api/postsales/v1/Wallet/payment-method/${paymentId}`,
      },
      M2M: {
        CASHOUT: '/api/postsale/v1/wallet/cashout-full',
      },
    },
    CANCEL: {
      STATUS: (orderId: string) => `/api/postsale/v1/orders/cancellation/status/${orderId}`,
      REASONS: `/api/postsale/v1/orders/cancelreasons`,
      CONFIRM: (orderId: string) => `/api/postsale/v1/orders/cancellation/${orderId}`,
    },
    VOUCHERS_PROMOS: '/api/postsales/v1/Vouchers',
    MENU_ITEMS: (siteId: string) => `/api/postsale/menu/${siteId}/10`,
  },
  ADDRESS: {
    FAVOURITE: 'api/address/v1/favourite',
    CHANGE_ORDER_ADDRESS: (orderId) => `/api/postsale/v1/order/${orderId}/change-address`,
    CHANGE_PICKUP_POINT: (orderId) => `/api/postsale/v1/order/${orderId}/change-pickuppoint`,
    GET_DROP_POINTS: (orderId, carrierKey) => `/api/postsale/v1/Orders/${orderId}/Carriers/${carrierKey}`,
  },
  MEMBER: {
    GET_PREMIUM: '/frontservices/2.0/member/getpremiumattributes',
  },
  BRANDALERTS: {
    GET_SUBSCRIPTIONS: '/api/optins/v1/member/subscriptions/brandalerts',
  },
  BRAND: {
    GET_ALL: '/api/brand/v1/brands',
    GET_FAVS: '/api/brand/v1/favorite',
    SUBSCRIBE: '/api/brand/v1/subscribe',
    UNSUBSCRIBE: '/api/brand/v1/unsubscribe',
    SEARCH: (brandName: string) => `/api/brand/v1/search?query=${encodeURIComponent(brandName)}`,
  },
}

export const getMessage = (items: ItemType[]) =>
  items
    .filter((item) => item.checked && item.comment)
    .map((item) => `${item.id}: ${item.comment}`)
    .join('\r\n')
    .substr(0, 4000)

export const getReturnProduct = (items: ItemType[]) =>
  items
    .filter((item) => item.checked)
    .map((item) => ({
      productId: item.id,
      orderDetailId: item.orderDetailId,
      quantity: item.returnDetails instanceof Array ? null : item.returnDetails?.quantity,
      returnReason: {
        reasonId: item.reason.reasonId,
      },
    }))

const getDropPointDeclarationPayload = (items: ItemType[], deliveryMode: number) => ({
  message: getMessage(items),
  returnProducts: getReturnProduct(items),
  dropPoint: { deliveryMode },
})

const getHomePickupDeclarationPayload = (
  items: ItemType[],
  addressId: string,
  homePickupDate: number,
  homePickupPeriod: string,
  withHomePickupPeriod: boolean
) => {
  const homePickupTimeSpan = withHomePickupPeriod ? { homePickupTimeSpan: homePickupPeriod } : {}

  return {
    homePickup: {
      addressId,
      homePickupDate,
      ...homePickupTimeSpan,
    },
    message: getMessage(items),
    returnProducts: getReturnProduct(items),
  }
}

const getByMyselfPayload = (items: ItemType[]) => ({
  message: getMessage(items),
  returnProducts: getReturnProduct(items),
})

export const getConfirmRequestPayload = (
  deliveryType: DeliveryType,
  items: ItemType[],
  addressId: string,
  homePickupDateValue: number,
  homePickupPeriod: string,
  withHomePickupPeriod: boolean,
  deliveryMode: number
) => {
  switch (deliveryType) {
    case DeliveryType.BULKY_BY_MYSELF:
    case DeliveryType.BY_MYSELF:
      return getByMyselfPayload(items)
    case DeliveryType.HOME_PICKUP:
      return getHomePickupDeclarationPayload(items, addressId, homePickupDateValue, homePickupPeriod, withHomePickupPeriod)
    default:
      return getDropPointDeclarationPayload(items, deliveryMode)
  }
}

export const sumReturnDetailsQty = (returnDetails: ReturnDetailType[]) => {
  const returnQty = returnDetails.reduce((returnVal, returnDetail) => {
    return returnVal + returnDetail.quantity
  }, 0)
  return returnQty
}

/**
 * Delete property "quantity" from the first level of returnDetails
 */
export const getFilteredDetails = (returnDetails: { quantity: number } | ReturnDetailType[]): ReturnDetailType[] => {
  // note(juan.rueda): prototype, consider moving this logic into the backend
  const filtered = Object.keys(returnDetails)
    .filter((key) => key !== 'quantity')
    .reduce((obj, key) => {
      const tempObj = { ...obj }
      tempObj[key] = returnDetails[key]
      return tempObj
    }, {})
  return Object.values(filtered)
}

export const getAllItemsPrice = (items: ItemType[]) => {
  const totalPrice = items.reduce((val, item) => {
    let qty = 0

    if (!item.checked) {
      qty = sumReturnDetailsQty(getFilteredDetails(item.returnDetails))
    } else {
      const returnQty = sumReturnDetailsQty(getFilteredDetails(item.returnDetails))

      const returnDetailsQty = item.returnDetails instanceof Array ? 0 : item.returnDetails?.quantity
      const checkedQty = typeof returnDetailsQty === 'string' ? parseInt(returnDetailsQty, 10) : returnDetailsQty

      qty = checkedQty + returnQty
    }
    if (qty === 0) {
      return val
    }
    return val + item.price * (qty || 1)
  }, 0)

  return totalPrice
}

export const getCheckedItemsPrice = (items: ItemType[]) => {
  // note(juan.rueda): prototype, consider moving this logic into the backend
  const totalPrice = items
    .filter((item) => item.checked)
    .reduce((val, item) => {
      if (!item.checked) return val

      const returnDetailsQty = item.returnDetails instanceof Array ? 0 : item.returnDetails?.quantity
      const checkedQty = typeof returnDetailsQty === 'string' ? parseInt(returnDetailsQty, 10) : returnDetailsQty

      return val + item.price * (checkedQty || 1)
    }, 0)

  return totalPrice
}

export const getReasonById = (reasons: ParsedOptionsType[], id: number) => {
  const options = reasons.map((reason) => reason.options).flat()
  return options.find((option) => option.reasonId === id)
}

export const getIsVex = (siteId: number) => VEX_SITE_IDS.includes(siteId)
export const getIsPrivalia = (siteId: number) => PRIVALIA_SITE_ID.includes(siteId)
export const getIsPrivaliaSpain = (siteId: number) => siteId === SiteId.PRIVALIA_ES
export const getIsPrivaliaItaly = (siteId: number) => siteId === SiteId.PRIVALIA_IT
export const getIsVeepeePurpleTheme = (siteId: number) => VEEPEE_PURPLE_SITE_ID.includes(siteId)
export const getShowSocialNetworks = (siteId: number) =>
  [SiteId.PRIVALIA_ES, SiteId.PRIVALIA_IT, SiteId.VEEPEE_ES, SiteId.VEEPEE_IT].includes(siteId)

export const parsePPOReturnReasons = (reasons: { key: string; subjectId: number }[], i18n: (key: string) => string) =>
  reasons.map((reason) => ({
    label: i18n(reason.key),
    value: i18n(reason.key),
  }))

export const getSumSelQuantityPrices = (items: SelectedItemType[]) => {
  const totalAmount = items.reduce((value, item) => {
    const checkedQty = typeof item.selQuantity === 'string' ? parseInt(item.selQuantity, 10) : item.selQuantity
    return value + item.unitPrice * (checkedQty || 1)
  }, 0)

  return totalAmount
}

export const getPpoReturnItems = (items: PastOrderItems[], selectedItems: SelectedItemType[]) => {
  const newPpoItems = items.map((item) => {
    if (selectedItems.find((selItem) => selItem.productId === item.productId)) {
      const updatedItem = { ...item, isSelected: true }
      return updatedItem
    }
    return item
  })
  return newPpoItems
}

export const getBodyItems = (item: SelectedItemType) => `
Product/articleId: ${item.productId}
ProductName: ${item.productName}
Quantity: ${item.selQuantity}
Reason: ${item.selReason}
`
export const getBodyAddress = (deliveryAddress: AddressType) =>
  `${deliveryAddress.address_details} ${!deliveryAddress.address_extras ? '' : deliveryAddress.address_extras} ${
    deliveryAddress.zip_code
  } ${deliveryAddress.city}`

export const getBodyMiraklOrderId = (miraklOrderId: string) => `${!miraklOrderId ? '' : `MiraklOrderId: ${miraklOrderId}`}`
export const getBodyTimeslot = (homePickupPeriod: DeliveryPeriod) =>
  `Timeslot: ${homePickupPeriod === DeliveryPeriod.MORNING ? 'Mañana' : 'Tarde'}`

export const getPpoReturnBody = (
  dayjs: typeof import('dayjs'),
  selectedItems: SelectedItemType[],
  deliveryAddress: AddressType,
  homePickupDate: HomePickupDateType,
  homePickupPeriod: DeliveryPeriod,
  order: PastOrder,
  isPrivaliaSpain: boolean,
  message: string
) => {
  const bodyItems = selectedItems.reduce((returnValue, item: SelectedItemType) => `${returnValue}${getBodyItems(item)}`, '')
  const bodyAddress = getBodyAddress(deliveryAddress)
  const bodyPickupDate = dayjs.unix(homePickupDate.value).format('DD/MM/YYYY')
  const bodyTimeslot = getBodyTimeslot(homePickupPeriod)
  const bodyMiraklOrderId = getBodyMiraklOrderId(order.miraklOrderId)

  const body = `RETURN  
OrderId: ${order.orderId}
${bodyMiraklOrderId}
${bodyItems}    
Address: ${bodyAddress}
PickupDate: ${bodyPickupDate}
${!isPrivaliaSpain ? '' : bodyTimeslot}

Message: ${message}`
  return body
}

export const getPpoUrls = (isPrivalia: boolean, memberId: string, orderId: string) =>
  isPrivalia
    ? {
        invoiceUrl: `/memberaccount/api/privalia/invoice/${memberId}/${orderId}`,
        helpUrl: ROUTES.PAST_ORDER_CONTACT(orderId),
      }
    : {
        invoiceUrl: `/memberaccount/api/vex/invoice/${memberId}/${orderId}`,
        helpUrl: ROUTES.CONTACT_SELECTOR,
      }

export const getQtyOptions = (maxQty: number) => {
  const optionList = []
  for (let i = 1; i <= maxQty; i += 1) {
    optionList.push({ value: i, label: i })
  }
  return optionList
}

export const throwAsyncErrorOnlyPrivaliaUsers = async () => {
  throw new Error('wrong siteId, page only for Privalia Users')
}

export const getReturnSubjectId = (page: string) => {
  switch (page) {
    case ReturnFlowSteps.SPECIAL_CARRIER:
      return RETURN_SPECIAL_CARRIER_SUBJECT_ID
    case DeliveryType.NEGATIVE_REFUND:
      return RETURN_NEGATIVE_REFUND_SUBJECT_ID
    case ReturnFlowSteps.NOT_ELIGIBLE:
      return RETURN_EXPIRED_SUBJECT_ID
    case ReturnFlowSteps.NOT_MODIFIABLE:
      return RETURN_NOT_MODIFIABLE_SUBJECT_ID
    case ReturnFlowSteps.MODIFY_RETURN_SRM:
      return RETURN_DROP_POINT_SUBJECT_ID
    default:
      return RETURN_SUBJECT_ID
  }
}

export const getReturnSubjectTitle = (page: string, orderId: string) => {
  switch (page) {
    case ReturnFlowSteps.SPECIAL_CARRIER:
      return `Order ${orderId}: Bulky return [Web]`
    case DeliveryType.NEGATIVE_REFUND:
      return `Order ${orderId}: Negative refund [Web]`
    default:
      return `Order ${orderId}: Return [Web]`
  }
}

export const isNegativeRefund = (returnMethods: ReturnType[]) =>
  returnMethods.some((method) => method.type === DeliveryType.NEGATIVE_REFUND)

export const getSpecialDeliveryTypes = (returnMethods: ReturnType[]) =>
  returnMethods.filter((method) => SPECIAL_DELIVERY_TYPES.includes(method.type))

export const getReturnMethodByType = (returnMethods, type) => returnMethods.find((method) => method.type === type)

export const getDropPointCarrierByDeliveryMode = (dropPointMethod, selDeliveryMode) =>
  dropPointMethod.data.find((carrier) => carrier.deliveryMode === selDeliveryMode)

export const shouldGoToModifyContactForm = (parsedOrder: ParsedOrderType, flowStep: ReturnFlowSteps) => {
  if (parsedOrder.modify) return true

  return CONTACT_FORM_FLOW_STEPS.includes(flowStep)
}

export const translateHelpContactReasons = (reasons: ContactReasonsType[], i18n: (key: string) => string) =>
  reasons.map((reason) => {
    const translatedReason = i18n(reason.label)

    const translatedSubReasons = reason.subReasons.map((subReason) => {
      return { ...subReason, key: subReason.label, label: i18n(subReason.label) }
    })

    return {
      ...reason,
      key: reason.label,
      label: translatedReason,
      subReasons: translatedSubReasons,
    }
  })

export const addLabels = (i18n: (key: string) => string, orderList: OrderListType[]) =>
  orderList.map((order) => ({
    ...order,
    children: (
      <>
        <MainLabel>{order.operationName?.toLowerCase()}</MainLabel>
        <ExtraLabel>
          {order.date} - {i18n('help.contact_form.order.number.text')} {order.orderId} - {order.price}
        </ExtraLabel>
      </>
    ),
  }))

export const getLocaleDate = (date: string, locale: string, format: Intl.DateTimeFormatOptions, i18n: (key: string) => string) => {
  if (!date) return i18n('ppo.order_detail.tracking.delivery_date.not_available')

  return new Date(date).toLocaleDateString(locale, format)
}

export const getPostOfficeKey = (
  i18n: { (translationKey: string): string; (arg0: string, arg1: { carrier: string }): string },
  carrier: string
) => {
  if (window.document.documentMode) {
    // note (juan.rueda) code for IE because doesn't support Template Literals
    const key = 'returns.confirmation.status.step1.part3.post_office'
    const txt = i18n(key)
      // eslint-disable-next-line no-template-curly-in-string
      .split('${carrier}')
      .join(carrier)
    return txt.split(carrier)
  }

  const txt = i18n('returns.confirmation.status.step1.part3.post_office', {
    carrier,
  })
  return txt.split(carrier)
}

export const getTextWithCarrierName = (
  i18n: { (translationKey: string): string; (arg0: string, arg1: { carrier: string }): string },
  key: string,
  carrier: string
) => {
  if (window.document.documentMode) {
    // note (juan.rueda) code for IE because doesn't support Template Literals
    const txt = i18n(key)
      // eslint-disable-next-line no-template-curly-in-string
      .split('${carrier}')
      .join(carrier)
    return txt
  }

  return i18n(key, {
    carrier,
  })
}

export const prevStep = (
  isModifyContactFormPage: boolean,
  orderId: string,
  currentPage: string,
  currentFlowStep: ReturnFlowSteps,
  setCurrentPage: (page: string) => void,
  setCurrentStep: (step: number) => void,
  currentStep: number
) => {
  if (currentPage === DeliveryType.ARBITRATION) return goOrders()

  if (isModifyContactFormPage) {
    switch (currentPage) {
      case DeliveryType.NEGATIVE_REFUND:
        setCurrentPage(RETURN)
        return setCurrentStep(1)
      case ReturnFlowSteps.RETURN_MENU:
        setCurrentPage(RETURN)
        return setCurrentStep(0)
      default:
        return goOrders()
    }
  }

  switch (currentStep) {
    case 0:
    case 4:
      return goOrders()
    case 1:
      if (currentFlowStep === ReturnFlowSteps.RETURN_MENU) {
        setCurrentPage(RETURN)
        return setCurrentStep(0)
      }
      return goOrders()
    case 2:
    case 3:
      return setCurrentStep(currentStep - 1)
    default:
      return undefined
  }
}

export const getFieldNameByType = (field_type: number) => {
  switch (field_type) {
    case 0:
      return 'address.form.name'
    case 1:
      return 'address.form.surname'
    case 2:
      return 'address.form.street_and_number'
    case 7:
      return 'address.form.zip_code'
    case 8:
      return 'address.form.city'
    case 9:
      return 'address.form.phone'
    default:
      return ''
  }
}

export const getMessageByFieldsErrors = (fields_errors: FieldErrorType[]) => {
  if (!fields_errors) return 'errors.address.not_saved.notification'
  if (fields_errors.length === 1) {
    if (fields_errors[0].field_type === 7) {
      // Forbidden zipcode error
      if (fields_errors[0].error_type === 4) {
        return 'errors.address.not_saved.forbidden_zipcode.notification'
      }
      // Unknown zipcode error
      if (fields_errors[0].error_type === 5) return 'errors.address.wrong_zipcode.notification'
    }

    return 'errors.address.single_wrong_field.notification'
  }
  if (fields_errors.length > 1) return 'errors.address.several_wrong_fields.notification'

  return 'errors.address.not_saved.notification'
}

const getFieldName = (message: string, fields_errors: FieldErrorType[]) =>
  message === 'errors.address.single_wrong_field.notification' ? getFieldNameByType(fields_errors[0].field_type) : undefined

export const getNotificationError = (fields_errors: FieldErrorType[]) => {
  const message = getMessageByFieldsErrors(fields_errors)
  return {
    message,
    hasError: true,
    fieldName: getFieldName(message, fields_errors),
  }
}

export const historyBack = () => window.history.back()

export const getFormKeys = (type: FormType) =>
  type === FormType.HELP_FORM
    ? ['help.contact_form.title', 'help.contact_form.text', 'help.contact_form.cta']
    : ['help.chat.title', 'help.chat.text', 'help.chat.cta']

export const getExpiredDate = (orderDate: string) => {
  // note(juan.rueda): this expirationDate is 2 months after orderDate
  if (!orderDate) return false
  const orderDateObj = new Date(orderDate)
  const expirationDate = new Date(orderDateObj.setMonth(orderDateObj.getMonth() + 2))
  const currentDate = new Date()
  if (currentDate > expirationDate) return true
  return false
}

export const getPageCardCols = (device: Device) => {
  switch (device) {
    case Device.DESKTOP:
      return 6
    case Device.TABLET:
      return 10
    default:
      return 12
  }
}

export const formatMaskedLabel = (maskedLabel: string) => {
  const labelLastNumbers = maskedLabel.replaceAll('*', '')
  return `• • • • ${labelLastNumbers}`
}

export const parseResContactMethods = (resContactMethods: ContactMethodType[]) => {
  const whatsapp = resContactMethods.find((contactMethod) => contactMethod.platformName === ContactMethods.WHATSAPP)
  const phone = resContactMethods.find((contactMethod) => contactMethod.platformName === ContactMethods.PHONE)
  const messenger = resContactMethods.find((contactMethod) => contactMethod.platformName === ContactMethods.MESSENGER)
  const parsedContactMethods = {
    whatsapp,
    phone,
    messenger,
  }
  return parsedContactMethods
}

export const convertArrayToObject = (array: ConfigApiType[], key: string) =>
  array.reduce((acc, curr) => {
    acc[curr[key]] = curr
    return acc
  }, {})

export const getRecycleReasons = () => RECYCLE_CONTACT_REASONS

export const getNotificationContainer = () => document.getElementById(NOTIFICATION_CONTAINER)

export const getVouchersPromosText = (isPromotion: boolean) => {
  const title = isPromotion ? 'vouchers.title.promotions' : 'vouchers.title.my_vouchers'
  const emptyText = isPromotion ? 'vouchers.text.promitons.emptyview' : 'vouchers.text.vouchers.emptyview'
  return { title, emptyText }
}

export const getMgmUrlImage = (device: Device, siteId: number) => {
  const isPrivalia = getIsPrivalia(siteId)
  const theme = isPrivalia ? Themes.PRIVALIA : Themes.VEEPEE_DEFAULT
  const isMobile = device === Device.MOBILE

  switch (theme) {
    case Themes.PRIVALIA:
      return isMobile
        ? 'https://media.veepee.com/v1/media/9cb957cd-6881-44ae-94b0-41137b0536d6'
        : 'https://media.veepee.com/v1/media/49e26824-ddca-4f4d-80e2-256c3d3e2bba'

    default:
      return isMobile
        ? 'https://media.veepee.com/v1/media/6d445f7c-f575-46a9-96ca-789c89bb5003'
        : 'https://media.veepee.com/v1/media/74e28fcc-90a1-4c25-8f69-4a6a93c93c92'
  }
}

export const getRefundTextKey = (deliveryType: string) => {
  switch (deliveryType) {
    case DeliveryType.BULKY_BY_MYSELF:
      return 'returns.special_items.we_will_refund_you.text'
    case DeliveryType.BY_MYSELF:
      return 'returns.we_will_refund_you.text'
    default:
      return 'returns.summary.refund.text'
  }
}

export const getIsByMyselfReturn = (deliveryType: string) => {
  const byMySelf: string[] = [DeliveryType.BULKY_BY_MYSELF, DeliveryType.BY_MYSELF]
  return byMySelf.includes(deliveryType)
}

const snakeToCamel = (str: string) => str.replace(/_(.)/g, (group) => group.replace('_', '').toUpperCase())

const mapObjectWithCase = (obj, mapper) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => mapObjectWithCase(v, mapper))
  } else if (obj != null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [mapper(key)]: mapObjectWithCase(obj[key], mapper),
      }),
      {}
    )
  }
  return obj
}
export const mapSnakeToCamelCase = (obj) => mapObjectWithCase(obj, snakeToCamel)

export const getIsMapDisabled = (carriers) => !!carriers.some((carrier) => !carrier.returnServiceId)

export const isFeatureEnabled = (config, featName) => config?.find((feature) => feature.name === featName)?.enabled

export const isWalletScreen = (screen) => Object.values(WalletScreen).includes(screen)

export const getTrackingCommValues = (selChannel, selSchedule) => {
  let commMethod = 'None'
  let commTime = 'None'

  switch (selChannel) {
    case BrandAlertsSubs.channel.EMAIL:
      commMethod = 'Email'
      break
    case BrandAlertsSubs.channel.PUSH:
      commMethod = 'Notif'
      break
    case BrandAlertsSubs.channel.EMAIL_AND_PUSH:
      commMethod = 'Email Notif'
      break
  }

  switch (selSchedule) {
    case BrandAlertsSubs.schedule.PREVIOUS_DAY:
      commTime = 'Day Before Opening'
      break
    case BrandAlertsSubs.schedule.OPENING_DAY:
      commTime = 'Opening Day'
      break
    case BrandAlertsSubs.schedule.PREVIOUS_AND_OPENING_DAY:
      commTime = 'Both'
      break
  }

  return { commMethod, commTime }
}

export const getAllBrandsKeys = (onlyFavBrands) => {
  if (onlyFavBrands) {
    return {
      headerkey: 'favorites.favorite_brands.title',
      titlekey: 'favorites.favorite_brands.title',
      textkey: 'favorites.favorite_brands.text',
    }
  }
  return {
    headerkey: 'favorites.all_brands.title',
    titlekey: 'favorites.all_brands.title',
    textkey: 'favorites.all_brands.text',
  }
}

export const getExpirationDate = (date, locale) => {
  const lowercaseDate = dayjs(date).locale(locale).format('DD-MMM-YYYY HH:mm')
  const month = dayjs(date).locale(locale).format('MMM')
  const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1)
  return lowercaseDate.replace(month, capitalizedMonth)
}

/**
 * Phone mask for Spain -> 3 digits "123 456 789"
 */
export const addSpainPhoneMask = (phone: string): string =>
  phone
    .replace(/\s+/g, '')
    .match(/.{1,3}/g)
    .join(' ')

/**
 * Phone mask for France -> 2 digits "12 34 56 78 90"
 */
export const addFrancePhoneMask = (phone: string): string =>
  phone
    .replace(/\s+/g, '')
    .match(/.{1,2}/g)
    .join(' ')

/**
 * Phone mask for Italy -> 3+ the rest "123 456789"
 */
export const addItalyPhoneMask = (phone: string): string => {
  const rawPhone = phone.replace(/\s+/g, '')
  const part01 = rawPhone.match(/^.{3,3}/g)
  if (part01) {
    const part02 = rawPhone.replace(part01[0], '')
    return [part01, part02].join(' ')
  }
  return phone
}

/**
 * Phone mask for Germany -> 2 digits+ the rest "12 3456789"
 */
export const addGermanyPhoneMask = (phone: string): string => {
  const rawPhone = phone.replace(/\s+/g, '')
  const part01 = rawPhone.match(/^.{2,2}/g)
  if (part01) {
    const part02 = rawPhone.replace(part01[0], '')
    return [part01, part02].join(' ')
  }
  return phone
}

export const addPhoneMask = (phone: string, siteId: SiteId): string => {
  if (!phone) return phone

  switch (siteId) {
    case SiteId.PRIVALIA_ES:
    case SiteId.VEEPEE_ES:
      return addSpainPhoneMask(phone)
    case SiteId.PRIVALIA_IT:
    case SiteId.VEEPEE_IT:
      return addItalyPhoneMask(phone)
    case SiteId.VEEPEE_FR:
      return addFrancePhoneMask(phone)
    case SiteId.VEEPEE_DE:
      return addGermanyPhoneMask(phone)
    default:
      return phone
  }
}

export const isNullOrUndefined = (value: number) => {
  return value === null || value === undefined
}

export const findImage = (images: { size: string; url: string }[], size: string) => images?.find((img) => img.size === size)?.url

export const getContentParts = (splitContentBySeller?: string[]): string[] => {
  if (!splitContentBySeller) {
    return []
  }
  if (splitContentBySeller?.[0]?.includes('${date}')) {
    const splitContentByDate = splitContentBySeller?.[0].split('${date}')
    return [splitContentByDate[0], splitContentByDate[1], splitContentBySeller[1]]
  }
  if (splitContentBySeller?.[1]?.includes('${date}')) {
    const splitContentByDate = splitContentBySeller?.[1].split('${date}')
    return [splitContentBySeller[0], splitContentByDate[0], splitContentByDate[1]]
  }

  return splitContentBySeller
}

/**
 * JS Implementation of MurmurHash3 (r136) (as of May 20, 2011)
 *
 * @author <a href="mailto:gary.court@gmail.com">Gary Court</a>
 * @see http://github.com/garycourt/murmurhash-js
 * @author <a href="mailto:aappleby@gmail.com">Austin Appleby</a>
 * @see http://sites.google.com/site/murmurhash/
 */
export const murmurhash3_32 = (key: string, seed: number) => {
  let h1: number, h1b: number, k1: number, i: number

  key = key.toString()

  const remainder = key.length & 3 // key.length %
  const bytes = key.length - remainder
  h1 = seed
  const c1 = 0xcc9e2d51
  const c2 = 0x1b873593
  i = 0
  while (i < bytes) {
    k1 =
      (key.charCodeAt(i) & 0xff) |
      ((key.charCodeAt(++i) & 0xff) << 8) |
      ((key.charCodeAt(++i) & 0xff) << 16) |
      ((key.charCodeAt(++i) & 0xff) << 24)
    ++i

    k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff
    k1 = (k1 << 15) | (k1 >>> 17)
    k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff

    h1 ^= k1
    h1 = (h1 << 13) | (h1 >>> 19)
    h1b = ((h1 & 0xffff) * 5 + ((((h1 >>> 16) * 5) & 0xffff) << 16)) & 0xffffffff
    h1 = (h1b & 0xffff) + 0x6b64 + ((((h1b >>> 16) + 0xe654) & 0xffff) << 16)
  }

  k1 = 0

  switch (remainder) {
    case 3:
      k1 ^= (key.charCodeAt(i + 2) & 0xff) << 16
      break
    case 2:
      k1 ^= (key.charCodeAt(i + 1) & 0xff) << 8
      break
    case 1:
      k1 ^= key.charCodeAt(i) & 0xff

      k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff
      k1 = (k1 << 15) | (k1 >>> 17)
      k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff
      h1 ^= k1
      break
  }
  h1 ^= key.length
  h1 ^= h1 >>> 16
  h1 = ((h1 & 0xffff) * 0x85ebca6b + ((((h1 >>> 16) * 0x85ebca6b) & 0xffff) << 16)) & 0xffffffff
  h1 ^= h1 >>> 13
  h1 = ((h1 & 0xffff) * 0xc2b2ae35 + ((((h1 >>> 16) * 0xc2b2ae35) & 0xffff) << 16)) & 0xffffffff
  h1 ^= h1 >>> 16

  const res = h1 >>> 0
  return res
}

export const getStepperTimelineLabel = (statusTimeline: StatusTimelineEventType[], locale: string) => {
  dayjs.locale(locale ?? 'en')

  const getFormattedDatesFromTimeline = (statusTimeline: StatusTimelineEventType[]): PeriodMapping => {
    if (!statusTimeline) return {}
    return statusTimeline?.reduce((acc, { id, startDate, endDate }) => {
      acc[id] = {
        startDate: startDate ? dayjs(startDate).format('DD/MM') : '',
        endDate: endDate ? dayjs(endDate).format('DD/MM') : '',
      }
      return acc
    }, {})
  }

  const formattedStatusDates = getFormattedDatesFromTimeline(statusTimeline)
  return { formattedStatusDates }
}

export const getCarrierName = (carrierId: string) => {
  if (!carrierId) return ''

  switch (carrierId) {
    case CarrierIdValue.COL:
      return 'Colissimo'
    case CarrierIdValue.MRL:
      return 'Mondial Relay'
    case CarrierIdValue.RCL:
      return 'Relais Colis'
    case CarrierIdValue.COR:
      return 'Correos'
    case CarrierIdValue.PIT:
      return 'Poste Italiane'
    case CarrierIdValue.ASM:
      return 'ASM'
    case CarrierIdValue.GLS:
      return 'GLS'
    case CarrierIdValue.MRW:
      return 'MRW'
    case CarrierIdValue.BPOST:
      return 'Bpost'
    case CarrierIdValue.INPOST:
      return 'InPost'
    case CarrierIdValue.AT_DHL_CONNECT:
    case CarrierIdValue.NL_DHL_CONNECT:
      return 'DHL Connect'
    case CarrierIdValue.DHL_AT:
    case CarrierIdValue.DHL_DE:
      return 'DHL'
    case CarrierIdValue.POST_NL:
      return 'PostNL'
    case CarrierIdValue.CHRPST:
      return 'Chronopost'
    case CarrierIdValue.UPS:
      return 'UPS'
    case CarrierIdValue.TNT:
      return 'TNT'
    case CarrierIdValue.ES_PAACK:
      return 'Paack'
    default:
      return carrierId
  }
}

export const startWithBasePath = (link: string, basePath: string) => link?.startsWith(basePath)

export const removeBasePath = (link: string, basePath: string) => link.toLowerCase().replace(basePath, '')
